import { InfoBanner, Input } from "@screencloud/screencloud-ui-components";
import { type InjectedIntlProps, injectIntl } from "react-intl";
import { Field, FieldRow, VatInput } from "../styles";

export interface PoVatFieldsProps {
  poNumber: string;
  vatNumber: string;
  onPoNumberChange: (value: string) => void;
  onVatNumberChange: (value: string) => void;
  /** Billing country code (ISO alpha-2), used as the VAT input label. */
  billingCountry: string;
  /** Show the VAT input (VAT-eligible countries: EU + GB). */
  showVatInput: boolean;
  /** Show the "sales tax will be added" banner instead (US/CA). */
  showTaxBanner: boolean;
}

/**
 * Billing-only PO number and VAT number fields, rendered after the City row of
 * the billing address form (GRWBKK-1704).
 *
 * PO number is always shown (optional, applied to invoices). The VAT area is
 * country-dependent, mirroring the billing model (`src/billinglatest/forms/Vat`):
 * VAT-eligible countries (EU + GB) get a VAT input labelled with their country
 * code; US/CA get a sales-tax info banner instead; everything else shows
 * neither. VAT itself is validated by Chargebee at checkout, so no frontend
 * validation happens here.
 */
const PoVatFieldsBase = ({
  poNumber,
  vatNumber,
  onPoNumberChange,
  onVatNumberChange,
  billingCountry,
  showVatInput,
  showTaxBanner,
  intl,
}: PoVatFieldsProps & InjectedIntlProps) => {
  const placeholder = (id: string, defaultMessage: string) =>
    intl.formatMessage({ id, defaultMessage });

  return (
    <>
      <FieldRow>
        <Field>
          <Input
            fluid
            name="poNumber"
            placeholder={placeholder(
              "pixi_wizard.payment_shipping.field.po_number",
              "PO number (optional)",
            )}
            value={poNumber}
            onChange={(_event, data) => onPoNumberChange(data.value)}
            data-testid="billing-po-number"
          />
        </Field>
      </FieldRow>
      {showTaxBanner && (
        <FieldRow>
          <Field data-testid="billing-tax-banner">
            <InfoBanner
              color="grey"
              description={placeholder(
                "pixi_wizard.payment_shipping.tax.sales_tax_added",
                "Sales tax will be added where required",
              )}
            />
          </Field>
        </FieldRow>
      )}
      {showVatInput && (
        <FieldRow>
          <Field>
            <VatInput
              fluid
              name="vatNumber"
              label={billingCountry}
              placeholder="123456789"
              value={vatNumber}
              onChange={(_event, data) => onVatNumberChange(data.value)}
              data-testid="billing-vat-number"
            />
          </Field>
        </FieldRow>
      )}
    </>
  );
};

export const PoVatFields = injectIntl(PoVatFieldsBase);
